import { PatientLayout } from "@/components/layout/patient-layout";
import { useListConditions } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Link } from "wouter";
import { Search, ArrowRight, BookOpen, Flame, Activity, Droplets, Gem, Layers, ArrowUpDown } from "lucide-react";
import { useMemo } from "react";
import { useSearchParam } from "@/hooks/use-search-param";
import { useScrollRestore } from "@/hooks/use-scroll-restore";
import { Highlight } from "@/components/ui/highlight";

const CONDITION_GROUPS: Record<string, { label: string; description: string; icon: React.ReactNode; color: string; badgeColor: string }> = {
  inflammatory_arthritis: {
    label: "Inflammatory Arthritis",
    description: "Conditions where the immune system attacks the joints",
    icon: <Flame className="h-4 w-4" />,
    color: "bg-rose-100 text-rose-700",
    badgeColor: "bg-rose-50 border-rose-200 text-rose-700",
  },
  connective_tissue: {
    label: "Connective Tissue Disorders",
    description: "Conditions affecting multiple body systems and tissues",
    icon: <Layers className="h-4 w-4" />,
    color: "bg-purple-100 text-purple-700",
    badgeColor: "bg-purple-50 border-purple-200 text-purple-700",
  },
  vasculitis: {
    label: "Vasculitis",
    description: "Conditions involving inflammation of blood vessels",
    icon: <Activity className="h-4 w-4" />,
    color: "bg-amber-100 text-amber-700",
    badgeColor: "bg-amber-50 border-amber-200 text-amber-700",
  },
  crystal_arthropathy: {
    label: "Crystal Arthropathy",
    description: "Conditions caused by crystals depositing in joints",
    icon: <Gem className="h-4 w-4" />,
    color: "bg-teal-100 text-teal-700",
    badgeColor: "bg-teal-50 border-teal-200 text-teal-700",
  },
  other: {
    label: "Other Conditions",
    description: "Other conditions managed by the rheumatology team",
    icon: <Droplets className="h-4 w-4" />,
    color: "bg-slate-100 text-slate-700",
    badgeColor: "bg-slate-50 border-slate-200 text-slate-600",
  },
};

const GROUP_ORDER = ["inflammatory_arthritis", "connective_tissue", "vasculitis", "crystal_arthropathy", "other"];

const FILTER_LABELS: Record<string, string> = {
  inflammatory_arthritis: "Inflammatory Arthritis",
  connective_tissue: "Connective Tissue",
  vasculitis: "Vasculitis",
  crystal_arthropathy: "Crystal Arthropathy",
  other: "Other",
};

export default function PatientKnowYourCondition() {
  useScrollRestore("patient-kyc");
  const [search, setSearch] = useSearchParam("q", "");
  const [activeType, setActiveType] = useSearchParam("type", "", { storageKey: "patient-kyc:type" });
  const [sort, setSort] = useSearchParam("sort", "az", { storageKey: "patient-kyc:sort" });
  const { data: conditions, isLoading } = useListConditions({ audience: "patient" });

  const filtered = useMemo(() => {
    if (!conditions) return [];
    const q = search.toLowerCase();
    const base = conditions.filter(c => {
      const matchesSearch = !search ||
        c.name.toLowerCase().includes(q) ||
        (c.simpleName ?? "").toLowerCase().includes(q) ||
        (c.patientDescription ?? "").toLowerCase().includes(q) ||
        (c.keySymptoms ?? []).some(s => s.toLowerCase().includes(q));
      const matchesType = !activeType || c.conditionType === activeType;
      return matchesSearch && matchesType;
    });
    const getName = (c: typeof base[0]) => (c.simpleName ?? c.name).toLowerCase();
    if (sort === "za") return [...base].sort((a, b) => getName(b).localeCompare(getName(a)));
    return [...base].sort((a, b) => getName(a).localeCompare(getName(b)));
  }, [conditions, search, activeType, sort]);

  const grouped = useMemo(() => {
    return GROUP_ORDER
      .map(type => ({
        type,
        meta: CONDITION_GROUPS[type],
        items: filtered.filter(c => c.conditionType === type),
      }))
      .filter(g => g.items.length > 0);
  }, [filtered]);

  const availableTypes = useMemo(() => {
    if (!conditions) return [];
    const seen = new Set(conditions.map(c => c.conditionType));
    return GROUP_ORDER.filter(t => seen.has(t));
  }, [conditions]);

  const resultCount = filtered.length;

  if (isLoading) return <PatientLayout title="Know Your Condition"><LoadingScreen /></PatientLayout>;

  return (
    <PatientLayout title="Know Your Condition">
      {/* Header */}
      <div className="mb-8 max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-rose-100 p-2.5 rounded-2xl">
            <BookOpen className="h-6 w-6 text-rose-600" />
          </div>
          <p className="text-lg text-slate-600 leading-relaxed">
            Plain-language guides to help you understand your rheumatic condition — what it means, what to expect, and how to live well with it.
          </p>
        </div>
        <UkHealthDisclaimer compact />
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-6">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search conditions or symptoms…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveType("")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              activeType === ""
                ? "bg-rose-700 text-white border-rose-700 shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:border-rose-400"
            }`}
          >
            All
          </button>
          {availableTypes.map(type => (
            <button
              key={type}
              onClick={() => setActiveType(activeType === type ? "" : type)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                activeType === type
                  ? "bg-rose-700 text-white border-rose-700 shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-rose-400"
              }`}
            >
              {FILTER_LABELS[type] ?? type}
            </button>
          ))}
          <button
            onClick={() => setSort(sort === "az" ? "za" : "az")}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-white text-slate-600 border-slate-200 hover:border-rose-400 inline-flex items-center gap-1.5 transition-all"
          >
            <ArrowUpDown className="h-3 w-3" />
            {sort === "az" ? "A–Z" : "Z–A"}
          </button>
        </div>
      </div>

      <p className={`mb-6 text-sm ${resultCount === 0 ? "text-rose-500" : "text-slate-500"}`}>
        {resultCount === 0 ? "No conditions found" : `Showing ${resultCount} ${resultCount === 1 ? "condition" : "conditions"}`}
      </p>

      {/* Grouped results */}
      {grouped.length === 0 && (
        <div className="py-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
          No conditions found{search ? ` matching "${search}"` : ""}.
        </div>
      )}

      {grouped.map(({ type, meta, items }) => (
        <div key={type} className="mb-10">
          {/* Group header */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-1.5 rounded-lg ${meta.color}`}>
              {meta.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-800">{meta.label}</h2>
                <span className={`text-xs font-semibold border rounded-full px-2.5 py-0.5 ${meta.badgeColor}`}>
                  {items.length}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{meta.description}</p>
            </div>
          </div>

          {/* Condition cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(c => (
              <Link key={c.id} href={`/patient/know-your-condition/${c.id}`}>
                <Card className="h-full hover:border-rose-300 hover:shadow-md transition-all cursor-pointer rounded-2xl group">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="mb-3">
                      <h3 className="font-bold text-slate-900 group-hover:text-rose-600 transition-colors leading-snug mb-0.5">
                        <Highlight text={c.simpleName ?? c.name} query={search} className="text-rose-700" />
                      </h3>
                      {c.simpleName && c.simpleName !== c.name && (
                        <p className="text-xs text-slate-400 mt-0.5">Medical name: <Highlight text={c.name} query={search} className="text-rose-700" /></p>
                      )}
                    </div>

                    <p className="text-slate-600 text-sm line-clamp-3 mb-4 flex-1 leading-relaxed">
                      <Highlight text={c.patientDescription ?? ""} query={search} className="text-rose-700" />
                    </p>

                    {/* Key symptoms */}
                    {c.keySymptoms && c.keySymptoms.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {c.keySymptoms.slice(0, 3).map((s, i) => (
                          <span key={i} className={`text-xs px-2 py-0.5 rounded-full border font-medium ${meta.badgeColor}`}>
                            {s}
                          </span>
                        ))}
                        {c.keySymptoms.length > 3 && (
                          <span className="text-xs px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-500 font-medium">
                            +{c.keySymptoms.length - 3} more
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-auto pt-3 border-t border-slate-100 flex items-center text-sm font-medium text-rose-600">
                      Read your guide <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </PatientLayout>
  );
}
